/**
 * TODO: Drag local file is not working, but clicking on the button is working
 * Add arrows between each of the columns or add a stepper
 * Fix cell width for file name
 * Add x icon in front of each keyword when we have remove filter associated with note
 * For manual input, don't allow user to input a keyword or owner that is in filters or owners
 * Make Table Scrollable -- https://smartdevpreneur.com/how-to-customize-material-ui-table-scrolling/
 * Make Summaries Scrollable
 * Fix Skeleton Loading for middle Column
 * Remove middle table headers
 * Align Summaries and Action Items file name to left and dropdown to the right
 *
 */

import React, {
	useState,
	useEffect,
	createContext,
	useContext,
	useMemo,
} from "react";

import {
	Box,
	Typography,
	Grid,
	Button,
	ButtonGroup,
	Avatar,
	Skeleton,
	CardHeader,
	Card,
	IconButton,
	Checkbox,
	Tooltip,
	Collapse,
	CardContent,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	ListItem,
	ListItemAvatar,
	ListItemText,
	Alert,
	Divider,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	LinearProgress,
} from "@mui/material";

import { useSelector } from "react-redux";

import { useDropzone } from "react-dropzone";

// Autocomplete imports
import Chip from "@mui/material/Chip";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import Link from "@mui/material/Link";
import LaunchIcon from "@mui/icons-material/Launch";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import NotesRounded from "@mui/icons-material/NotesRounded";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import CircleRounded from "@mui/icons-material/CircleRounded";
import { third_party_constants } from "constants/third_party_constants";
import throttle from "lodash/throttle";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import { getGoogleApiKey } from "api/google";

import { getWorkspaceAccessToken } from "api/user";

// table imports
// import Table from '@mui/material/Table';
// import TableBody from '@mui/material/TableBody';
// import TableCell from '@mui/material/TableCell';
// import TableContainer from '@mui/material/TableContainer';
// import TableHead from '@mui/material/TableHead';
// import TableRow from '@mui/material/TableRow';
// import Paper from '@mui/material/Paper';

// stepper imports
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";

import useDrivePicker from "react-google-drive-picker";

// context variables
const ParentCheckedContext = createContext();
const AcceptedRejectedContext = createContext();

/**
 * Components
 */

// Expandable component row in table in "File Uploaded" column
const ExpandedComponent = ({ file, boxesChecked, setBoxesChecked }) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const parentChecked = useContext(ParentCheckedContext);
	const [userFileFilters, setUserFileFilters] = useState([]);
	const [fileKeywords, setFileKeywords] = useState(file.keywords); // file.keywords

	const token = useSelector((state) => state.auth.token);

	const handleUserKeywordChange = (event, values) => {
		setUserFileFilters(values);
	};

	useEffect(() => {
		if (parentChecked) {
			if (!boxesChecked.includes(file.thirdPartyId)) {
				setBoxesChecked((boxesChecked) => [...boxesChecked, file.thirdPartyId]);
			}
		}
	}, [parentChecked]);

	const handleCheckboxChange = (event) => {
		const isChecked = event.target.checked;
		if (isChecked) {
			// add file.noteId to list of selected files
			setBoxesChecked((boxesChecked) => [...boxesChecked, file.thirdPartyId]);
		} else {
			// remove file.noteId to list of selected files
			setBoxesChecked(
				boxesChecked?.filter((noteId) => noteId !== file.thirdPartyId)
			);
		}
	};

	// on submit, remove the user file filter from the fileKeywords state and send to backend
	const handleRemoveKeyword = async (keyword) => {
		if (keyword) {
			const url = new URL(
				`${process.env.REACT_APP_PORT}/notes/removeKeywordsFromNotes`
			);

			try {
				const response = await fetch(url, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({
						noteIds: [file.thirdPartyId],
						keywordNames: [keyword],
					}),
				});
				const data = await response.json();

				if (response.status === 200) {
					const newFileKeywords = fileKeywords?.filter(
						(oldFilter) => oldFilter !== keyword
					);

					setFileKeywords(newFileKeywords);
				}
			} catch (error) {
				console.log(error);
			}
		}
	};

	// on submit, send userFileFilters to backend to create filters for a single file
	// remove from autocomplete list if success
	const handleSubmit = async (noteIds) => {
		// send userFileFilters to backend
		if (userFileFilters.length > 0) {
			const url = new URL(
				`${process.env.REACT_APP_PORT}/notes/addFiltersToNotes`
			);

			try {
				const response = await fetch(url, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({
						noteIds: noteIds,
						filterNames: userFileFilters,
					}),
				});
				const data = await response.json();

				if (data) {
					console.log("Successfully added filters to file");
					// check if userFileFilters in file.keywords, if a filter in userFileFilters is not in file.keywords, add it
					const newFileFilters = userFileFilters?.filter(
						(filter) => !fileKeywords.includes(filter)
					);

					setFileKeywords([...fileKeywords, ...newFileFilters]);

					setUserFileFilters([]);
				}
			} catch (error) {
				console.log(error);
			}
		}
	};

	return (
		<>
			<TableRow
				key={file.thirdPartyId}
				sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
			>
				{/* CHECKBOX */}
				<TableCell sx={{ padding: "0px" }}>
					<Checkbox
						onChange={handleCheckboxChange}
						checked={boxesChecked.includes(file.thirdPartyId)}
						sx={{ "& .MuiSvgIcon-root": { fontSize: 10, margin: 0.01 } }}
					/>
				</TableCell>

				{/* FILE NAME */}
				<TableCell align="left" sx={{ padding: "8px", maxWidth: "125px" }}>
					<Typography
						variant="tableBody"
						sx={{
							whiteSpace: "nowrap",
							textOverflow: "ellipsis",
							width: "125px",
							display: "block",
							overflow: "hidden",
						}}
					>
						{file?.name}
					</Typography>
				</TableCell>

				{/* DATE CREATED */}
				<TableCell align="left" sx={{ padding: "8px" }}>
					<Typography
						variant="tableBody"
						sx={{ fontStyle: "italic", fontSize: 10 }}
					>
						{new Date(file?.createdTime).toLocaleDateString("en-US", {
							month: "2-digit",
							day: "2-digit",
							year: "numeric",
						})}
					</Typography>
				</TableCell>

				{/* ACTIONS */}
				<TableCell align="center" sx={{ padding: "2px", alignContent: "left" }}>
					{/* OPEN FILE */}
					<Link href={file.link} target="_blank" rel="noopener">
						<Tooltip title="Open File" placement="top">
							<IconButton sx={{ padding: 1 }}>
								<LaunchIcon sx={{ width: 10, height: 10, marginRight: 0 }} />
							</IconButton>
						</Tooltip>
					</Link>
				</TableCell>

				{/* EXPAND */}
				<TableCell sx={{ padding: "0px" }}>
					<Typography variant="tableHeader" sx={{ padding: "0px" }}>
						<IconButton onClick={() => setIsExpanded(!isExpanded)}>
							{isExpanded ? (
								<KeyboardArrowUpIcon
									sx={{ width: 10, height: 10, marginRight: 0 }}
								/>
							) : (
								<KeyboardArrowDownIcon
									sx={{ width: 10, height: 10, marginRight: 0 }}
								/>
							)}
						</IconButton>
					</Typography>
				</TableCell>
			</TableRow>

			{/* EXPANDED ROW */}
			{isExpanded && (
				<TableRow>
					<TableCell
						colSpan={5}
						sx={{ justifyContent: "center", alignItems: "center" }}
					>
						<Box marginBottom="5px" justifyContent="left">
							<Typography
								variant="tableBody"
								sx={{ fontSize: 10, textTransform: "none" }}
							>
								Parent Folders:
							</Typography>
						</Box>

						{/* KEYWORDS */}
						<Box
							sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}
						>
							<Box marginBottom="5px" justifyContent="center">
								{file.parents?.map((parent) => (
									<Button
										// startIcon={<CloseIcon sx={{ width:10, height:10 }} />}
										variant="outlined"
										sx={{
											margin: "3px",
											fontSize: 10,
											textTransform: "none",
											borderRadius: 5,
										}}
										disabled
									>
										<Typography
											variant="tableBody"
											sx={{ fontSize: 10, textTransform: "none" }}
										>
											{parent?.name}
										</Typography>
									</Button>
								))}
							</Box>
						</Box>

						<Box marginBottom="5px" justifyContent="left">
							<Typography
								variant="tableBody"
								sx={{ fontSize: 10, textTransform: "none" }}
							>
								Owners:
							</Typography>
						</Box>

						{/* OWNERS */}
						<Box
							sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}
						>
							<Box marginBottom="5px" justifyContent="center">
								{file.owners?.map((owner) => (
									<Button
										startIcon={
											<AccountCircleIcon sx={{ width: 20, height: 20 }} />
										}
										variant="outlined"
										sx={{
											margin: "3px",
											fontSize: 10,
											textTransform: "none",
											borderRadius: 5,
										}}
									>
										<Typography
											variant="tableBody"
											sx={{ fontSize: 10, textTransform: "none" }}
										>
											{owner?.name}
										</Typography>
									</Button>
								))}
							</Box>
						</Box>

						<Box marginBottom="5px" justifyContent="left">
							<Box sx={{ display: "block" }}>
								<Typography
									variant="tableBody"
									sx={{ fontSize: 10, textTransform: "none" }}
								>
									Keywords:
								</Typography>
							</Box>
							<Box sx={{ display: "block" }}>
								<Typography
									variant="tableBody"
									sx={{ fontSize: 8, textTransform: "none" }}
								>
									Summarize File to Autogenerate Keywords:
								</Typography>
							</Box>
						</Box>

						{/* USER INDIVIDUAL NOTE KEYWORDS */}
						<Box
							sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}
						>
							<Box marginBottom="5px" justifyContent="center">
								{fileKeywords?.map((keyword) => (
									<Button
										startIcon={<CloseIcon sx={{ width: 10, height: 10 }} />}
										variant="outlined"
										onClick={() => handleRemoveKeyword(keyword)}
										sx={{
											margin: "3px",
											fontSize: 10,
											textTransform: "none",
											borderRadius: 5,
										}}
									>
										<Typography
											variant="tableBody"
											sx={{ fontSize: 10, textTransform: "none" }}
										>
											{keyword}
										</Typography>
									</Button>
								))}
							</Box>
						</Box>

						<Box
							sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}
						>
							{/* USER INPUT */}
							<Stack spacing={1} sx={{ width: "100%", padding: "0px" }}>
								<Autocomplete
									multiple
									id="tags-filled"
									options={[]}
									freeSolo
									value={userFileFilters}
									onChange={handleUserKeywordChange}
									renderTags={(value, getTagProps) =>
										value?.map((option, index) => (
											<Chip
												variant="outlined"
												label={option}
												{...getTagProps({ index })}
												size="small"
											/>
										))
									}
									renderInput={(params) => (
										<TextField
											{...params}
											// label="User Keywords"
											variant="filled"
											label={
												<Typography
													variant="tableBody"
													sx={{ fontSize: 12, textTransform: "none" }}
												>
													User Keywords
												</Typography>
											}
											placeholder="Enter User Keywords"
											size={"small"}
										/>
									)}
								/>
							</Stack>
						</Box>

						{/* USER FILE FILTERS */}
						<Box
							sx={{
								display: "flex",
								flexDirection: "row",
								flexWrap: "wrap",
								alignContent: "right",
							}}
						>
							{userFileFilters.length > 0 ? (
								<Button
									variant="contained"
									sx={{ mt: 1 }}
									onClick={() => handleSubmit([file.thirdPartyId])}
								>
									<Typography
										variant="tableBody"
										sx={{ fontSize: 10, textTransform: "none" }}
									>
										Create Keywords
									</Typography>
								</Button>
							) : (
								<Button variant="contained" sx={{ mt: 1 }} disabled>
									<Typography
										variant="tableBody"
										sx={{ fontSize: 10, textTransform: "none" }}
									>
										Create Keywords
									</Typography>
								</Button>
							)}
						</Box>
					</TableCell>
				</TableRow>
			)}
		</>
	);
};

const UploadedFileTable = ({
	uploadedFiles,
	handleSetSummarizedFilesAndActionItems,
	handleSetShowProgressBar,
}) => {
	const allWorkspaces = useSelector((state) => state.auth.user?.allWorkspaces);
	const [boxesChecked, setBoxesChecked] = useState([]);
	const [parentChecked, setParentChecked] = useState(false);
	const [isGlobalFiltersExpanded, setIsGlobalFiltersExpanded] = useState(false);
	const [userGlobalFilters, setUserGlobalFilters] = useState([]);
	const [globalFiltersCreated, setGlobalFiltersCreated] = useState([]);
	const token = useSelector((state) => state.auth.token);

	const handleGlobalFiltersExpand = () => {
		setIsGlobalFiltersExpanded(!isGlobalFiltersExpanded);
	};

	useEffect(() => {
		if (boxesChecked.length === uploadedFiles.length) {
			setParentChecked(true);
		} else {
			setParentChecked(false);
		}

		if (boxesChecked.length === 0) {
			setIsGlobalFiltersExpanded(false);
		}
	}, [boxesChecked]);

	const handleUserKeywordChange = (event, values) => {
		setUserGlobalFilters(values);
	};

	const handleParentCheckboxChange = (event) => {
		const isChecked = event.target.checked;
		if (isChecked) {
			// add file.noteId to list of selected files
			setParentChecked(true);
		} else {
			// remove file.noteId to list of selected files
			setBoxesChecked([]);
			setParentChecked(false);
		}
	};

	// on submit, remove the user global filter from the globalFiltersCreated state and send to backend
	const handleRemoveKeyword = async (keyword, noteIds) => {
		if (keyword) {
			const url = new URL(
				`${process.env.REACT_APP_PORT}/notes/removeKeywordsFromNotes`
			);

			try {
				const response = await fetch(url, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({
						noteIds: noteIds,
						keywordNames: [keyword],
					}),
				});
				const data = await response.json();

				if (response.status === 200) {
					const newGlobalFilters = globalFiltersCreated?.filter(
						(oldFilter) => oldFilter !== keyword
					);

					setGlobalFiltersCreated(newGlobalFilters);
				}
			} catch (error) {
				console.log(error);
			}
		}
	};

	// on submit, create global filters for all files and send to backend
	// remove from autocomplete list if success
	const handleSubmit = async (noteIds) => {
		if (userGlobalFilters.length > 0) {
			const url = new URL(
				`${process.env.REACT_APP_PORT}/notes/addFiltersToNotes`
			);

			try {
				const response = await fetch(url, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({
						noteIds: noteIds,
						filterNames: userGlobalFilters,
					}),
				});
				const data = await response.json();

				if (data) {
					// check if userGlobalFilters in globalFiltersCreated, if a filter in userFileFilters is not in file.keywords, add it

					const newGlobalFilters = userGlobalFilters?.filter(
						(filter) => !globalFiltersCreated.includes(filter)
					);
					setGlobalFiltersCreated([
						...globalFiltersCreated,
						...newGlobalFilters,
					]);

					setUserGlobalFilters([]);
				}
			} catch (error) {
				console.log(error);
			}
		}
	};

	/**
	 *
	 * TODO: handles summarizing and generating action items for the selected files
	 * Summaries and action items are to be processed in parallel
	 * They will be returned as different properties of the same response object
	 */
	const handleSummarizeSelectedFiles = async () => {
		try {
			handleSetShowProgressBar(true);
			const selectedFiles = Object.values(uploadedFiles)?.filter((file) =>
				boxesChecked.includes(file.thirdPartyId)
			);
			const response = await fetch(
				`${process.env.REACT_APP_PORT}/summarizer/getOpenAIResponse`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({ notes: selectedFiles }),
				}
			);

			const data = await response.json();

			if (data)
				data.data.forEach((file) => {
					console.log("adding for file", file);
					handleSetSummarizedFilesAndActionItems(file);
				});

			handleSetShowProgressBar(false);
		} catch (error) {
			handleSetShowProgressBar(false);
			console.log(
				"error in summarizing files and creating action items:",
				error
			);
		}
	};

	return (
		<ParentCheckedContext.Provider value={parentChecked}>
			<Box sx={{ width: "100%" }}>
				<TableContainer component={Paper}>
					<Table aria-label="simple table">
						{/* ACTIONS */}
						<TableHead>
							<TableCell
								colSpan={5}
								sx={{
									padding: "0px",
									justifyContent: "center",
									alignItems: "center",
								}}
							>
								{/* CHECKBOX -- SELECT ALL */}
								<Tooltip title="Check All Files" placement="top">
									<Checkbox
										sx={{
											"& .MuiSvgIcon-root": { fontSize: 10, margin: 0.01 },
										}}
										indeterminate={
											boxesChecked.length > 0 &&
											boxesChecked.length < uploadedFiles.length
										}
										checked={
											boxesChecked.length === uploadedFiles.length ||
											parentChecked
										}
										onChange={handleParentCheckboxChange}
										defaultChecked={
											boxesChecked.length === uploadedFiles.length ||
											parentChecked
										}
									/>
								</Tooltip>

								{boxesChecked.length > 0 && (
									<>
										{/* SUMMARIZE SELECTED FILES */}
										<Tooltip
											title="Summarize and Create Action Items for Selected Files"
											placement="top"
										>
											<IconButton onClick={handleSummarizeSelectedFiles}>
												<SendIcon sx={{ width: 10, height: 10 }} />
											</IconButton>
										</Tooltip>

										{/* ADD FILTERS TO ALL FILES */}
										<Tooltip
											title="Add Filters to Selected Files"
											placement="top"
											onClick={handleGlobalFiltersExpand}
										>
											<IconButton>
												{isGlobalFiltersExpanded ? (
													<AddCircleIcon
														color="primary"
														sx={{ width: 10, height: 10 }}
													/>
												) : (
													<AddIcon sx={{ width: 10, height: 10 }} />
												)}
											</IconButton>
										</Tooltip>
									</>
								)}
							</TableCell>
						</TableHead>

						{/* GLOBAL USER FILTERS */}
						<TableHead>
							<TableCell
								colSpan={5}
								sx={{
									padding: "0px",
									justifyContent: "center",
									alignItems: "center",
								}}
							>
								{isGlobalFiltersExpanded && (
									<>
										<Box
											sx={{
												display: "flex",
												flexDirection: "row",
												flexWrap: "wrap",
											}}
										>
											<Stack spacing={1} sx={{ width: "100%", padding: "0px" }}>
												<Autocomplete
													multiple
													id="tags-filled"
													options={[]}
													freeSolo
													value={userGlobalFilters}
													onChange={handleUserKeywordChange}
													renderTags={(value, getTagProps) =>
														value?.map((option, index) => (
															<Chip
																variant="outlined"
																label={option}
																{...getTagProps({ index })}
																size="small"
															/>
														))
													}
													renderInput={(params) => (
														<TextField
															{...params}
															// label="User Keywords"
															variant="filled"
															label={
																<Typography
																	variant="tableBody"
																	sx={{ fontSize: 12, textTransform: "none" }}
																>
																	User Keywords
																</Typography>
															}
															placeholder="Enter User Keywords"
															size={"small"}
														/>
													)}
												/>
											</Stack>
										</Box>

										<Box
											sx={{
												display: "flex",
												flexDirection: "row",
												flexWrap: "wrap",
												alignContent: "right",
											}}
										>
											{/* submit button if userGlobalFilters is greater than 0, else disabled button */}
											{userGlobalFilters.length > 0 ? (
												<Button
													variant="contained"
													sx={{ width: "40%", height: "100%", m: 1 }}
													onClick={() => handleSubmit(boxesChecked)}
												>
													<Typography
														variant="tableBody"
														sx={{ fontSize: 10, textTransform: "none" }}
													>
														Create Filters
													</Typography>
												</Button>
											) : (
												<Button
													variant="contained"
													sx={{ width: "40%", height: "100%", m: 1 }}
													disabled
												>
													<Typography
														variant="tableBody"
														sx={{ fontSize: 10, textTransform: "none" }}
													>
														Create Filters
													</Typography>
												</Button>
											)}
										</Box>

										{/* GLOBAL NOTE KEYWORDS */}
										<Box
											sx={{
												display: "flex",
												flexDirection: "row",
												flexWrap: "wrap",
											}}
										>
											<Box marginBottom="5px" justifyContent="center">
												{globalFiltersCreated?.map((keyword) => (
													<Button
														startIcon={
															<CloseIcon sx={{ width: 10, height: 10 }} />
														}
														variant="outlined"
														onClick={() =>
															handleRemoveKeyword(keyword, boxesChecked)
														}
														sx={{
															margin: "3px",
															fontSize: 10,
															textTransform: "none",
															borderRadius: 5,
														}}
													>
														<Typography
															variant="tableBody"
															sx={{ fontSize: 10, textTransform: "none" }}
														>
															{keyword}
														</Typography>
													</Button>
												))}
											</Box>
										</Box>
									</>
								)}
							</TableCell>
						</TableHead>

						{/* TABLE HEADER */}
						<TableHead>
							<TableRow>
								<TableCell sx={{ padding: "0px" }}>
									<Box
										sx={{
											"& .MuiSvgIcon-root": { fontSize: 10, margin: 0.01 },
										}}
									></Box>
								</TableCell>
								<TableCell align="left" sx={{ padding: "8px" }}>
									<Typography variant="tableHeader">Name</Typography>
								</TableCell>
								<TableCell align="left" sx={{ padding: "8px" }}>
									<Typography variant="tableHeader">Date</Typography>
								</TableCell>
								<TableCell align="center" sx={{ padding: "8px" }}>
									<Typography variant="tableHeader" sx={{ mr: 1 }}>
										View
									</Typography>
								</TableCell>
								<TableCell sx={{ padding: "0px" }}></TableCell>
							</TableRow>
						</TableHead>

						{/* TABLE BODY */}
						<TableBody>
							{uploadedFiles?.map((file) => (
								<ExpandedComponent
									file={file}
									boxesChecked={boxesChecked}
									setBoxesChecked={setBoxesChecked}
								/>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>
		</ParentCheckedContext.Provider>
	);
};

const SkeletonGroupFileUploaded = ({ number }) => {
	const skeletonGroups = [];
	for (let i = 0; i < number; i++) {
		skeletonGroups.push(
			<Box key={i} pb="2rem">
				<Box
					sx={{ width: "100%", display: "flex" }}
					pb="0.5rem"
					justifyContent="center"
				>
					<Skeleton
						variant="circular"
						width={30}
						height={30}
						sx={{ mr: 0.5 }}
					/>
					<Skeleton
						variant="circular"
						width={30}
						height={30}
						sx={{ mr: 0.5 }}
					/>
					<Skeleton
						variant="rectangular"
						width={180}
						height={30}
						sx={{ borderRadius: "20px", mr: 0.5 }}
					/>
					<Skeleton
						variant="rectangular"
						width={30}
						height={30}
						sx={{ borderRadius: "5px" }}
					/>
				</Box>
				<Box sx={{ width: "100%", display: "flex" }} justifyContent="center">
					<Skeleton
						variant="rectangular"
						width={60}
						height={30}
						sx={{ borderRadius: "45%", mr: 0.5 }}
					/>
					<Skeleton
						variant="rectangular"
						width={60}
						height={30}
						sx={{ borderRadius: "45%", mr: 0.5 }}
					/>
					<Skeleton
						variant="rectangular"
						width={60}
						height={30}
						sx={{ borderRadius: "45%", mr: 0.5 }}
					/>
					<Skeleton
						variant="rectangular"
						width={60}
						height={30}
						sx={{ borderRadius: "45%" }}
					/>
				</Box>
			</Box>
		);
	}

	return <Box>{skeletonGroups}</Box>;
};

const SkeletonGroupSummarizeFiles = ({ number }) => {
	const skeletonGroups = [];
	for (let i = 0; i < number; i++) {
		skeletonGroups.push(
			<Box key={i} pb="2rem">
				<Box
					sx={{ width: "100%", display: "flex" }}
					pb="0.5rem"
					justifyContent="center"
				>
					<Skeleton
						variant="circular"
						width={30}
						height={30}
						sx={{ mr: 0.5 }}
					/>
					<Skeleton
						variant="circular"
						width={30}
						height={30}
						sx={{ mr: 0.5 }}
					/>
					<Skeleton
						variant="rectangular"
						width={165}
						height={30}
						sx={{ borderRadius: "20px" }}
					/>
				</Box>
				<Box sx={{ width: "100%", display: "flex" }} justifyContent="center">
					<Skeleton
						variant="rectangular"
						width={230}
						height={150}
						sx={{ borderRadius: "10%" }}
					/>
				</Box>
			</Box>
		);
	}

	return <Box>{skeletonGroups}</Box>;
};

const LocalUploadCard = () => {
	const handleDrop = (acceptedFiles) => {
		console.log(acceptedFiles);
	};

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop: handleDrop,
		accept: "*",
		maxSize: 5242880,
	});

	const handleClick = () => {
		console.log("Local upload button clicked!");
	};

	return (
		<Box pl="6rem" pr="6rem" justifyContent="center">
			<Box
				{...getRootProps()}
				style={{ width: "100%" }}
				pb="2rem"
				justifyContent="center"
			>
				<input {...getInputProps()} />
				<Button
					variant="outlined"
					sx={{
						textTransform: "none",
						borderRadius: 5,
						justifyContent: "left",
						width: "90%",
						border: "dashed",
						padding: 2,
					}}
				>
					{/* WORKSPACE NAME */}
					<Typography
						variant="h6"
						sx={{
							mr: 1,
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						Local Upload (Select or Drag)
					</Typography>
				</Button>
			</Box>
		</Box>
	);
};

const ActionItemComponent = ({ actionItemObj, card, id }) => {
	const actionItem = actionItemObj?.actionItem;
	const { acceptedRejectedItems, setAcceptedRejectedItems } = useContext(
		AcceptedRejectedContext
	);

	const isAccepted = acceptedRejectedItems[id][actionItem]?.isAccepted;
	const isRejected = acceptedRejectedItems[id][actionItem]?.isRejected;

	const handleAccept = (state) => {
		setAcceptedRejectedItems((prevState) => ({
			...prevState,
			[id]: {
				...prevState[id],
				[actionItem]: {
					...prevState[id][actionItem],
					isAccepted: state,
				},
			},
		}));
	};

	const handleReject = (state) => {
		setAcceptedRejectedItems((prevState) => ({
			...prevState,
			[id]: {
				...prevState[id],
				[actionItem]: {
					...prevState[id][actionItem],
					isRejected: state,
				},
			},
		}));
	};

	const bgcolor =
		!isAccepted && !isRejected
			? "primary.container"
			: isAccepted && !isRejected
			? "primary.main"
			: "surface.variant";

	return (
		<Box
			sx={{ width: "100%", display: "flex", marginBottom: "3px" }}
			justifyContent="left"
		>
			<Box
				sx={{
					width: "100%",
					display: "flex",
					bgcolor: bgcolor,
					borderRadius: 2,
				}}
				justifyContent="left"
			>
				{!isAccepted && !isRejected && (
					<>
						<IconButton onClick={() => handleAccept(true)}>
							<CheckIcon sx={{ width: "10px", height: "10px" }} />
						</IconButton>

						<IconButton onClick={() => handleReject(true)}>
							<ClearIcon sx={{ width: "10px", height: "10px" }} />
						</IconButton>

						<Typography
							variant="tableBody"
							sx={{ mr: 1, padding: "7px", maxWidth: "275px" }}
						>
							{actionItem}
						</Typography>
					</>
				)}
				{isAccepted && !isRejected && (
					<>
						<Button
							variant="text"
							onClick={() => handleAccept(false)}
							sx={{ textTransform: "none", fontSize: 11, color: "white" }}
						>
							Undo
						</Button>
						<Typography
							variant="tableBody"
							sx={{
								mr: 1,
								padding: "7px",
								maxWidth: "250px",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
								color: "white",
							}}
						>
							{actionItem}
						</Typography>
					</>
				)}
				{isRejected && (
					<>
						<Button
							variant="text"
							onClick={() => handleReject(false)}
							sx={{ textTransform: "none", fontSize: 11 }}
						>
							Undo
						</Button>
						<Typography
							variant="tableBody"
							sx={{
								mr: 1,
								padding: "7px",
								maxWidth: "250px",
								textDecoration: "line-through",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{actionItem}
						</Typography>
					</>
				)}
			</Box>
		</Box>
	);
};

const FinalCardTableRows = ({
	content,
	card,
	boxesChecked,
	setBoxesChecked,
}) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const parentChecked = useContext(ParentCheckedContext);

	useEffect(() => {
		if (parentChecked) {
			if (!boxesChecked.includes(content.noteId)) {
				setBoxesChecked((boxesChecked) => [...boxesChecked, content.noteId]);
			}
		}
	}, [parentChecked]);

	const handleCheckboxChange = (event) => {
		const isChecked = event.target.checked;
		if (isChecked) {
			// add file.noteId to list of selected files
			setBoxesChecked((boxesChecked) => [...boxesChecked, content.noteId]);
		} else {
			// remove file.noteId to list of selected files
			setBoxesChecked(
				boxesChecked.filter((noteId) => noteId !== content.noteId)
			);
		}
	};

	return (
		<>
			<TableRow
				key={content.noteId}
				sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
			>
				{/* CHECKBOX */}
				{card !== "Action Items" && (
					<TableCell sx={{ padding: "0px" }}>
						<Checkbox
							onChange={handleCheckboxChange}
							checked={boxesChecked.includes(content.noteId)}
							sx={{ "& .MuiSvgIcon-root": { fontSize: 10, margin: 0.01 } }}
						/>
					</TableCell>
				)}

				{/* FILE NAME */}
				<TableCell align="left" sx={{ padding: "8px" }}>
					<Typography
						variant="tableBody"
						sx={{
							whiteSpace: "nowrap",
							textOverflow: "ellipsis",
							width: "175px",
							display: "block",
							overflow: "hidden",
						}}
					>
						{content.fileName}
					</Typography>
				</TableCell>

				{/* EXPAND */}
				<TableCell sx={{ padding: "0px" }}>
					<Typography variant="tableHeader" sx={{ padding: "0px" }}>
						<IconButton onClick={() => setIsExpanded(!isExpanded)}>
							{isExpanded ? (
								<KeyboardArrowUpIcon
									sx={{ width: 10, height: 10, marginRight: 0 }}
								/>
							) : (
								<KeyboardArrowDownIcon
									sx={{ width: 10, height: 10, marginRight: 0 }}
								/>
							)}
						</IconButton>
					</Typography>
				</TableCell>
			</TableRow>

			{/* EXPANDED ROWS */}
			{isExpanded && (
				<TableRow>
					<TableCell sx={{ padding: "5px" }} colSpan={3}>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "left",
							}}
						>
							{card === "Summaries" ? (
								<Typography
									variant="tableBody"
									sx={{ padding: "0px", whiteSpace: "pre-line" }}
								>
									{content.summary}
								</Typography>
							) : (
								<Typography variant="tableBody" sx={{ padding: "0px" }}>
									{content?.actionItems?.map((actionItem) => (
										<ActionItemComponent
											actionItemObj={actionItem}
											card={card}
											id={content.noteId}
										/>
									))}
								</Typography>
							)}
						</Box>
					</TableCell>
				</TableRow>
			)}
		</>
	);
};

const FinalCardComponent = ({
	summarizedFilesArr,
	actionItemsArr,
	card,
	handleSetActionItems,
}) => {
	const token = useSelector((state) => state.auth.token);
	const allWorkspaces = useSelector((state) => state.auth.user?.allWorkspaces);

	const [open, setOpen] = useState(false);
	const [boxesChecked, setBoxesChecked] = useState([]);
	const [parentChecked, setParentChecked] = useState(false);
	const [openActionDialog, setOpenActionDialog] = useState(false);
	const [summarizedFiles, setSummarizedFiles] = useState([]);
	const [actionItems, setActionItems] = useState([]);

	const [personState, setPersonState] = useState([]);
	const [options, setOptions] = useState([]);
	const [isTyping, setIsTyping] = useState(false); // exclusively for the loading circular progress wheel
	const [actionItemIndexMap, setActionItemIndexMap] = useState({});
	const [dateState, setDateState] = useState({});
	const [autoCompleteKey, setAutoCompleteKey] = useState(0); // enables hot reload for the autocomplete component

	const [acceptedRejectedItems, setAcceptedRejectedItems] = useState(() => {
		const initialState = {};
		if (card === "Action Items") {
			actionItemsArr?.forEach((item) => {
				const noteId = item.noteId;
				const actionItemList = item.actionItems;
				if (actionItemList.length > 0) {
					const actionItemsObj = {};
					actionItemList.forEach((actionItem) => {
						actionItemsObj[actionItem.actionItem] = {
							isAccepted: false,
							isRejected: false,
						};
					});
					initialState[noteId] = actionItemsObj;
				}
			});
		}
		return initialState;
	});

	// set action items and summarized files
	useEffect(() => {
		setActionItems(actionItemsArr);
		setSummarizedFiles(summarizedFilesArr);
	}, [actionItemsArr, summarizedFilesArr]);

	useEffect(() => {
		const newState = { ...acceptedRejectedItems };
		if (card === "Action Items") {
			actionItemsArr?.forEach((item) => {
				const noteId = item.noteId;
				const actionItemList = item.actionItems;
				if (actionItemList.length > 0) {
					const actionItemsObj = {};
					actionItemList.forEach((actionItem) => {
						actionItemsObj[actionItem.actionItem] = {
							isAccepted: false,
							isRejected: false,
						};
					});
					newState[noteId] = actionItemsObj;
				}
			});
		}

		setAcceptedRejectedItems(newState);
	}, [actionItemsArr, summarizedFilesArr]);

	useEffect(() => {
		console.log("acceptedRejectedItems are", actionItemsArr);
	}, [acceptedRejectedItems]);

	const handleOpen = (func) => {
		func(true);
	};

	/**
	 *
	 * TODO: handles submitting all of the action items with their respective users
	 * Action items will be assigned to each email address that is submitted under an item
	 */
	const handleSubmitActionItem = async (actionItem) => {
		console.log("submitting action item", actionItem);
		try {
			const response = await fetch(
				`${process.env.REACT_APP_PORT}/actionItems/createActionItem`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({ actionItem: actionItem }),
				}
			);

			const data = await response.json();
			// if (data) console.log("data is", data);
			return true;
		} catch (error) {
			return false;
		}
	};

	const handleClose = async (func, confirmActionItems) => {
		// remove action items from acceptedRejectedItems that are rejected and that were accepted
		if (confirmActionItems && func === setOpenActionDialog) {
			let newAcceptedRejectedItems = { ...acceptedRejectedItems };
			let actionItemsNew = actionItemsArr;
			Object.keys(newAcceptedRejectedItems)?.forEach(async (noteId) => {
				const actionItemsObj = newAcceptedRejectedItems[noteId];
				Object.keys(actionItemsObj)?.forEach(async (actionItem) => {
					const isAccepted = actionItemsObj[actionItem]?.isAccepted;
					const isRejected = actionItemsObj[actionItem]?.isRejected;

					// submit the action items
					if (isAccepted) {
						const submittedItem = Object.values(actionItemsNew)
							?.filter((item) => item.noteId === noteId)
							?.map((item2) => {
								return item2.actionItems?.filter(
									(actionItemObj) => actionItemObj.actionItem === actionItem
								);
							})[0];

						const otherActionItemData = actionItems?.filter(
							(item) => item.noteId === noteId
						);

						// make sure all assignees are properly input
						// input can be weird with autocomplete / button input,
						// so we need to check manually for arrays as inputs
						const assignees = [];
						personState[actionItemIndexMap[actionItem]].forEach((item) => {
							if (Array.isArray(item)) {
								item.forEach((input) => assignees.push(input));
							} else assignees.push(item);
						});

						await handleSubmitActionItem({
							submittedItem: submittedItem[0],
							workspaceId: otherActionItemData[0].workspaceId,
							noteId: otherActionItemData[0].noteId,
							assignees: assignees,
							dueDate: dateState[actionItem],
							title: otherActionItemData[0].fileName,
						});
					}

					if (isRejected || isAccepted) {
						Object.values(actionItemsNew)
							?.filter((item) => item.noteId === noteId)
							?.map((item2) => {
								const newActionItems = item2.actionItems?.filter(
									(actionItemObj) => actionItemObj.actionItem !== actionItem
								);
								item2.actionItems = newActionItems;
							});

						delete actionItemsObj[actionItem];

						// TODO: MODIFY PERSON STATE, DATE STATE, AND ACTION ITEM INDEX MAP
						// REMOVE ACTION ITEM FROM PERSON STATE,
						const index = actionItemIndexMap[actionItem];
						const personStateNew = personState.splice(index, index);
						setPersonState(personStateNew);

						// REMOVE ITEM FROM DATE STATE
						let dateStateNew = { ...dateState };
						delete dateStateNew[actionItem];
						setDateState(dateStateNew);

						// MAKE EVERY INDEX ABOVE MAPPED ACTION ITEM TO INDEX-1 AND REMOVE ACTION ITEM FROM MAP
						let actionItemIndexMapNew = { ...actionItemIndexMap };
						delete actionItemIndexMapNew[actionItem];
						Object.keys(actionItemIndexMapNew)?.map((idx) =>
							idx > index ? idx - 1 : idx
						);
						setActionItemIndexMap(actionItemIndexMapNew);
					}
				});
			});

			handleSetActionItems(actionItemsNew);
			setAcceptedRejectedItems(newAcceptedRejectedItems);
		}
		func(false);
	};
	// return true if at least one action item is accepted

	const showActionCreate = Object.values(acceptedRejectedItems).some(
		(actionItemsObj) =>
			Object.values(actionItemsObj).some(
				(item) => item.isAccepted || item.isRejected
			)
	);

	const currentContent = card === "Summaries" ? summarizedFiles : actionItems;

	useEffect(() => {
		if (boxesChecked.length === currentContent.length) {
			setParentChecked(true);
		} else {
			setParentChecked(false);
		}

		if (boxesChecked.length === 0) {
			// setIsGlobalFiltersExpanded(false);
		}
	}, [boxesChecked]);

	const handleParentCheckboxChange = (event) => {
		const isChecked = event.target.checked;
		if (isChecked) {
			// add file.noteId to list of selected files
			setParentChecked(true);
		} else {
			// remove file.noteId to list of selected files
			setBoxesChecked([]);
			setParentChecked(false);
		}
	};

	const [userFilters, setUserFilters] = useState([]);

	const handleUserKeywordChange = (event, values) => {
		setUserFilters(values);
	};

	const handleDateChange = (id, date) => {
		let newDateState = dateState;

		if (!newDateState[id]) newDateState[id] = date;
		else newDateState[id] = date;
		setDateState(newDateState);
	};

	const handleValueChange = (event, id, person, reason) => {
		let newPersonState = personState;
		let newActionItemIndexMap = actionItemIndexMap;

		let value;
		if (typeof person === "object") {
			value = person?.map((item) =>
				item.email ? item.email : item
			);
		} else value = person.email ? person.email : person;

		if (reason === "selectOption") {
			if (!newActionItemIndexMap.hasOwnProperty(id)) {
				newActionItemIndexMap[id] = newPersonState.length;
				newPersonState.push([value]);
			} else {
				// 2 cases: user input or recommended button click
				// if user input, the value will be an array with all of the new values
				// if button click, the value will just be a string of the recommended value
				// check the value:
				// - if it's an array, check the values in the current person state and set to new array,
				// - if it's a string, check the person state to see if it has the value already
				if (!newPersonState[actionItemIndexMap[id]].includes(value)) {
					if (typeof value !== "object")
						newPersonState[actionItemIndexMap[id]] = [
							...newPersonState[actionItemIndexMap[id]],
							value,
						];
					else newPersonState[actionItemIndexMap[id]] = value;
				}
			}
		}
		// remove option
		else {
			newPersonState[actionItemIndexMap[id]] = value;
		}

		// set state variables
		setAutoCompleteKey(autoCompleteKey + 1);
		setActionItemIndexMap(newActionItemIndexMap);
		setPersonState(newPersonState);
	};

	const handleAutocompleteChange = async (event, value) => {
		let query = event.target.value;
		if (!query) {
			query = "";
		}
		if (query.length > 0) {
			query = query.trim();
		}
		setIsTyping(query.length > 0); // exclusively for the loading circular progress wheel

		if (query !== "") {
			// clear old options
			setOptions([]);

			// fetch new options
			let url = new URL(`${process.env.REACT_APP_PORT}/people/searchPeople`);
			url.searchParams.set("query", query);
			const response = await fetch(url, {
				method: "GET",
				headers: { Authorization: `Bearer ${token}` },
			});
			const data = await response.json();

			const workspaceEmails = [];
			Object.values(allWorkspaces).forEach((workspace) =>
				workspaceEmails.push({
					email: workspace.email,
                    displayName: workspace.displayName,
					resource: "",
					photo: workspace.profilePicturePath,
				})
			);

			if (data.results) {
				setOptions([...data.results, ...workspaceEmails]);
			} else {
				console.log("error");
			}
		}
	};

	const throttledHandleAutocompleteChange = useMemo(
		() => throttle(handleAutocompleteChange, 1000),
		[]
	);

	// Stop the invocation of the throttled function after unmounting
	useEffect(() => {
		return () => {
			throttledHandleAutocompleteChange.cancel();
		};
	}, []);

	return (
		<>
			<AcceptedRejectedContext.Provider
				value={{ acceptedRejectedItems, setAcceptedRejectedItems }}
			>
				<ParentCheckedContext.Provider value={parentChecked}>
					<Card sx={{ borderRadius: "16px", marginBottom: "15px" }}>
						{/* "SUMMARIES" OR "ACTION ITEMS" */}
						<CardHeader
							title={
								<Typography
									variant="h6"
									sx={{
										whiteSpace: "nowrap",
										overflow: "hidden",
										textOverflow: "ellipsis",
										textAlign: "left",
									}}
								>
									{card}
								</Typography>
							}
							action={
								<IconButton onClick={() => setOpen(!open)}>
									{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
								</IconButton>
							}
						/>
						<Collapse in={open} timeout="auto" unmountOnExit>
							<CardContent padding="12px">
								<TableContainer component={Paper}>
									<Table aria-label="simple table">
										{/* USER ACTIONS */}
										<TableHead>
											<TableCell
												colSpan={3}
												sx={{
													padding: "0px",
													justifyContent: "center",
													alignItems: "center",
												}}
											>
												{/* CHECKBOX -- SELECT ALL */}
												{card !== "Action Items" && (
													<Tooltip title={`Check All ${card}`} placement="top">
														<Checkbox
															sx={{
																"& .MuiSvgIcon-root": {
																	fontSize: 10,
																	margin: 0.01,
																},
															}}
															indeterminate={
																boxesChecked.length > 0 &&
																boxesChecked.length < currentContent.length
															}
															checked={
																boxesChecked.length === currentContent.length ||
																parentChecked
															}
															onChange={handleParentCheckboxChange}
															defaultChecked={
																boxesChecked.length === currentContent.length ||
																parentChecked
															}
														/>
													</Tooltip>
												)}

												{/* SUMMARIES AND BOXESCHECKED > 0 */}
												{card === "Summaries" && boxesChecked.length > 0 && (
													<Tooltip title="Download Summaries" placement="top">
														<IconButton>
															<DownloadIcon sx={{ width: 10, height: 10 }} />
														</IconButton>
													</Tooltip>
												)}

												{/* ACTION ITEMS AND SHOWACTIONCREATE */}
												{card === "Action Items" && showActionCreate && (
													<Tooltip title="Confirm Action Items" placement="top">
														<IconButton
															onClick={() => handleOpen(setOpenActionDialog)}
														>
															<SendIcon sx={{ width: 10, height: 10 }} />
														</IconButton>
													</Tooltip>
												)}

												{/* ACTION ITEMS AND ACTIONITEMDIALOG */}
												{card === "Action Items" && (
													<Dialog
														open={openActionDialog}
														onClose={() => handleClose(setOpenActionDialog)}
														aria-labelledby="alert-dialog-title"
														aria-describedby="alert-dialog-description"
														sx={{
															width: "100%",
															height: "100%",
															minWidth: "1000px",
														}}
													>
														<DialogTitle id="alert-dialog-title">
															{"Confirm Action Items"}
														</DialogTitle>
														<DialogContent>
															<DialogContentText id="alert-dialog-description">
																{/* For each ActionItem that has isAccepted to true, select a user to assign it to */}
																{actionItems &&
																	actionItems?.map((content) => (
																		<Box>
																			{content?.actionItems?.map(
																				(actionItem) => (
																					<Box sx={{ mb: "1rem" }}>
																						{acceptedRejectedItems[
																							content?.noteId
																						][actionItem?.actionItem]
																							?.isAccepted && (
																							<Box>
																								<Typography variant="h6">
																									{actionItem?.actionItem}
																								</Typography>
																								<Stack
																									spacing={1}
																									sx={{
																										width: "100%",
																										padding: "0px",
																									}}
																								>
																									<Autocomplete
																										key={autoCompleteKey}
																										multiple
																										// freeSolo
																										sx={{
																											margin: "1rem",
																										}}
																										componentsProps={{
																											popper: {
																												placement:
																													"bottom-start",
																												style: { width: 300 },
																											},
																										}}
																										/* Throttling Autocomplete */
																										filterOptions={(x) => x} // disable filtering on client
																										loading={isTyping}
																										loadingText="Finding assingees..."
																										onInputChange={
																											throttledHandleAutocompleteChange
																										}
																										options={options}
																										value={
																											personState[
																												actionItemIndexMap[
																													actionItem.actionItem
																												]
																											]
																												? personState[
																														actionItemIndexMap[
																															actionItem
																																.actionItem
																														]
																												  ]
																												: []
																										}
																										limitTags={1}
																										getOptionLabel={(option) => { console.log("option is", option); return (
																											option.email ||
																											option.displayName ||
																											(Array.isArray(option) && option[0]) ||
																											(typeof option !== 'object' && typeof option === 'string' && option) ||
																											'undefined'
																										)}}
																										onChange={(
																											e,
																											value,
																											reason
																										) =>
																											handleValueChange(
																												e,
																												actionItem?.actionItem,
																												value,
																												reason
																											)
																										}
																										renderInput={(params) => (
																											<div>
																												<Box
																													display="flex"
																													sx={{
																														width: "100%",
																														justifyContent:
																															"center",
																														alignItems:
																															"center",
																													}}
																												>
																													<TextField
																														{...params}
																														// label="User Keywords"
																														variant="filled"
																														label={
																															<Typography
																																variant="tableBody"
																																sx={{
																																	fontSize: 14,
																																}}
																															>
																																Assign to People
																															</Typography>
																														}
																														placeholder="Enter User Keywords"
																														size={"medium"}
																														sx={{ mr: "1rem" }}
																													/>
																													<Box>
																														<LocalizationProvider
																															dateAdapter={
																																AdapterDateFns
																															}
																														>
																															<DatePicker
																																label="Set Due Date"
																																value={
																																	dateState[
																																		actionItem
																																			?.actionItem
																																	]
																																}
																																onChange={(
																																	newValue
																																) =>
																																	handleDateChange(
																																		actionItem?.actionItem,
																																		newValue
																																	)
																																}
																																sx={{
																																	mr: "1rem",
																																}}
																															/>
																														</LocalizationProvider>
																													</Box>
																												</Box>
																												<Box
																													display="flex"
																													sx={{
																														alignItems:
																															"center",
																														mt: "1rem",
																													}}
																												>
																													<Typography variant="h6">
																														Recommended
																														Assignee:{" "}
																													</Typography>
																													<Button
																														startIcon={
																															<AccountCircleIcon
																																sx={{
																																	width: 20,
																																	height: 20,
																																}}
																															/>
																														}
																														variant="outlined"
																														sx={{
																															margin: "3px",
																															fontSize: 10,
																															textTransform:
																																"none",
																															borderRadius: 5,
																															ml: "0.5rem",
																														}}
																														onClick={(e) =>
																															handleValueChange(
																																e,
																																actionItem?.actionItem,
																																actionItem?.assignee,
																																"selectOption"
																															)
																														}
																													>
																														<Typography
																															variant="tableBody"
																															sx={{
																																fontSize: 10,
																																textTransform:
																																	"none",
																															}}
																														>
																															{
																																actionItem?.assignee
																															}
																														</Typography>
																													</Button>
																												</Box>
																											</div>
																										)}
																										renderOption={(
																											props,
																											option
																										) => { return (
																											<ListItem
																												{...props}
																												key={
																													option?.resourceName
																												}
																												dense
																											>
																												<ListItemAvatar>
																													<Avatar
																														src={option?.photo}
																														sx={{
																															width: 34,
																															height: 34,
																														}}
																													/>
																												</ListItemAvatar>
																												<ListItemText
																													primary={
																														option?.displayName ? option?.displayName :
																														option?.email ? option?.email :
																														'undefined'
																													}
																													secondary={
																														option?.email ? option?.email :
																														''
																													}
																													primaryTypographyProps={{
																														variant: "h6",
																													}}
																													secondaryTypographyProps={{
																														variant: "h6",
																														color:
																															"text.secondary",
																													}}
																												/>
																											</ListItem>
																										); }}
																									/>
																								</Stack>
																							</Box>
																						)}
																						{acceptedRejectedItems[
																							content?.noteId
																						][actionItem?.actionItem]
																							?.isRejected && (
																							<Box sx={{ mb: "1rem" }}>
																								<Typography
																									variant="h6"
																									color="error"
																								>
																									Removing action item:{" "}
																									{actionItem?.actionItem}
																								</Typography>
																							</Box>
																						)}
																					</Box>
																				)
																			)}
																		</Box>
																	))}
															</DialogContentText>
														</DialogContent>
														<DialogActions>
															<Button
																variant="outlined"
																size="small"
																onClick={() => handleClose(setOpenActionDialog)}
															>
																Close
															</Button>
															<Button
																variant="contained"
																size="small"
																onClick={() =>
																	handleClose(setOpenActionDialog, true)
																}
																autoFocus
															>
																Confirm
															</Button>
														</DialogActions>
													</Dialog>
												)}
											</TableCell>
										</TableHead>

										{/* TABLE BODY */}
										<TableBody>
											{card === "Summaries"
												? summarizedFiles?.map((content) => (
														<FinalCardTableRows
															content={content}
															card={card}
															boxesChecked={boxesChecked}
															setBoxesChecked={setBoxesChecked}
														/>
												  ))
												: actionItems?.map((content) => (
														<FinalCardTableRows
															content={content}
															card={card}
															boxesChecked={boxesChecked}
															setBoxesChecked={setBoxesChecked}
														/>
												  ))}
										</TableBody>
									</Table>
								</TableContainer>
							</CardContent>
						</Collapse>
					</Card>
				</ParentCheckedContext.Provider>
			</AcceptedRejectedContext.Provider>
		</>
	);
};

const ChooseWorkspaceComponent = ({
	selectedWorkspace,
	setSelectedWorkspace,
	workspaceId,
	uploadedFiles,
	setUploadedFiles,
	setOpenNotification,
	duplicates,
	setDuplicates,
	handleSetShowUploadBar,
}) => {
	const currentWorkspaces = useSelector(
		(state) => state.auth.user?.currentWorkspaces
	);
	const allWorkspaces = useSelector((state) => state.auth.user?.allWorkspaces);

	const token = useSelector((state) => state.auth.token);
	const userid = useSelector((state) => state.auth.user?._id);

	// GOOGLE PICKER FUNCTIONS
	const [openGooglePicker, data, authResponse] = useDrivePicker();

	const handleDocsData = async (data, workspaceId) => {
		// if documents were uploaded
		if (data && data.docs) {
			// get the ids of all the docs into an array
			const docs = Array.from(data.docs)?.map((doc) => {
				return doc.id;
			});

			// if the docs array is not empty
			if (docs) {
				handleSetShowUploadBar(true);
				let url = new URL(`${process.env.REACT_APP_PORT}/notes/create`);

				const response = await fetch(url, {
					method: "POST",
					headers: {
						Authorization: `Bearer ${token}`,
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						docs: data.docs,
						workspaceId: workspaceId,
					}),
				});

				const dataRes = await response.json();
				console.log('datares is', dataRes);

				// for each of the createdFiles, add them to the uploadedFiles array
				if (dataRes.createdFiles) {
					const uploadedFilesNew = [...uploadedFiles, ...dataRes.createdFiles];
					setUploadedFiles(uploadedFilesNew);
					handleSetShowUploadBar(false);
				}

				const newDuplicateFiles = [];
				const newDuplicateFolders = [];

				// for each of the duplicateFiles, add them to the duplicates array
				if (dataRes.duplicateFiles.length > 0) {
					for (const duplicateFile of dataRes.duplicateFiles) {
						newDuplicateFiles.push(duplicateFile);
					}
				}

				// for each of the duplicateFolders, add them to the duplicates array
				if (dataRes.duplicateFolders.length > 0) {
					for (const duplicateFolders of dataRes.duplicateFolders) {
						newDuplicateFolders.push(duplicateFolders);
					}
				}

				// if there are duplicates, set the duplicates state to the new duplicates
				if (
					dataRes.duplicateFiles.length > 0 ||
					dataRes.duplicateFolders.length > 0
				) {
					setDuplicates({
						duplicateFiles: [
							...duplicates.duplicateFiles,
							...newDuplicateFiles,
						],
						duplicateFolders: [
							...duplicates.duplicateFolders,
							...newDuplicateFolders,
						],
					});
					setOpenNotification(true);
				}
			} else console.log("no data available");
		}
	};

	const handleOpenGooglePicker = async (workspaceId) => {
		if (workspaceId) {
			if (allWorkspaces[workspaceId].thirdPartyType === "google") {
				const GOOGLE_ACCESS_TOKEN = await getWorkspaceAccessToken(
					token,
					workspaceId
				);
				const GOOGLE_API_KEY = await getGoogleApiKey(token);

				openGooglePicker({
					clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
					developerKey: GOOGLE_API_KEY,
					viewId: "DOCS",
					setIncludeFolders: true,
					setSelectFolderEnabled: true,
					token: GOOGLE_ACCESS_TOKEN,
					showUploadView: true,
					showUploadFolders: true,
					supportDrives: true,
					multiselect: true,
					callbackFunction: async (data) => {
						if (data.action === "cancel") {
							console.log("User clicked cancel/close button");
							setSelectedWorkspace(null);
						}
						if (data) await handleDocsData(data, workspaceId);
					},
				});
			}
		}
	};

	const handleClick = async (item, setItem) => {
		if (setItem === setSelectedWorkspace) {
			setSelectedWorkspace(item);
			await handleOpenGooglePicker(item);
		}
	};

	return (
		<Box style={{ width: "100%" }} pb="2rem" justifyContent="center">
			<Button
				variant={workspaceId === selectedWorkspace ? "contained" : "outlined"}
				sx={{
					textTransform: "none",
					borderRadius: 5,
					justifyContent: "left",
					width: "90%",
				}}
				onClick={() => handleClick(workspaceId, setSelectedWorkspace)}
			>
				{/* WORKSPACE PROFILE PICTURE */}
				<Avatar
					src={allWorkspaces[workspaceId].profilePicturePath}
					sx={{ width: 25, height: 25, ml: 1, mt: 1, mb: 1, mr: 0.5 }}
				/>

				{/* PROFILE PICTURE */}
				{third_party_constants[allWorkspaces[workspaceId].thirdPartyType].icon({
					sx: { mr: 0.5, width: 25, height: 25 },
				})}

				{/* WORKSPACE NAME */}
				<Typography
					variant="h6"
					sx={{
						mr: 1,
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}
				>
					{allWorkspaces[workspaceId].email}
				</Typography>
			</Button>
		</Box>
	);
};

const UploadPage = () => {
	const [selectedWorkspace, setSelectedWorkspace] = useState(null);
	const [uploadedFiles, setUploadedFiles] = useState([]);
	const [summarizedFiles, setSummarizedFiles] = useState([]);
	const [actionItems, setActionItems] = useState([]);
	const [openNotification, setOpenNotification] = useState(false);
	const [duplicates, setDuplicates] = useState({
		duplicateFiles: [],
		duplicateFolders: [],
	});

	const currentWorkspaces = useSelector(
		(state) => state.auth.user?.currentWorkspaces
	);
	const allWorkspaces = useSelector((state) => state.auth.user?.allWorkspaces);

	// const [showThirdColumn, setShowThirdColumn] = useState(false);
	const [showProgressBar, setShowProgressBar] = useState(false);
	const [showUploadBar, setShowUploadBar] = useState(false);

	const handleSetActionItems = (value) => {
		setActionItems(value);
	};

	const handleSetShowProgressBar = (value) => {
		setShowProgressBar(value);
	};

	const handleSetShowUploadBar = (value) => {
		setShowUploadBar(value);
	};

	const handleSetSummarizedFilesAndActionItems = (file) => {
		// check duplicate object by noteId
		if (!summarizedFiles.some((item) => item.noteId === file.fileId)) {
			setSummarizedFiles((prevState) => [
				...prevState,
				{
					noteId: file.fileId,
					fileName: file.fileName,
					summary: file.summary,
					workspaceId: file.workspaceId,
				},
			]);

			setActionItems((prevState) => [
				...prevState,
				{
					noteId: file.fileId,
					fileName: file.fileName,
					actionItems: file.actionItems,
					workspaceId: file.workspaceId,
				},
			]);
		}
	};

	const cards = ["Summaries", "Action Items"];

	const UploadProgressBar = () => {
		return (
			<Box sx={{ width: "50%" }}>
				<LinearProgress sx={{ borderRadius: 10, height: "10px" }} />
			</Box>
		);
	};

	const DuplicatesNotification = (props) => {
		return (
			<Box
				sx={{
					margin: "10px",
					fontSize: 10,
					textTransform: "none",
					borderRadius: 10,
					mt: "1rem",
					ml: "1rem",
					mr: "1rem",
				}}
			>
				<Collapse
					in={openNotification}
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Alert
						severity="warning"
						action={
							<IconButton
								aria-label="close"
								severity="warning"
								size="small"
								onClick={() => {
									setOpenNotification(false);
									setDuplicates({
										duplicateFiles: [],
										duplicateFolders: [],
									});
								}}
							>
								<CloseIcon fontSize="inherit" />
							</IconButton>
						}
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						{duplicates?.duplicateFiles.length > 0 &&
							duplicates?.duplicateFolders.length > 0 && (
								<>
									Found {props.duplicates?.duplicateFiles.length} duplicate file
									{props.duplicates?.duplicateFiles.length > 1
										? "s"
										: ""} and {props.duplicates?.duplicateFolders.length} folder
									{props.duplicates?.duplicateFolders.length > 1 ? "s" : ""}
								</>
							)}
						{duplicates?.duplicateFiles.length > 0 &&
							duplicates?.duplicateFolders.length === 0 && (
								<>
									Found {props.duplicates?.duplicateFiles.length} duplicate file
									{props.duplicates?.duplicateFiles.length > 1 ? "s" : ""}
								</>
							)}
						{duplicates?.duplicateFiles.length === 0 &&
							duplicates?.duplicateFolders.length > 0 && (
								<>
									Found {props.duplicates?.duplicateFolders.length} folder
									{props.duplicates?.duplicateFolders.length > 1 ? "s" : ""}
								</>
							)}

						{props.duplicates?.duplicateFiles?.map((duplicateFile) => {
							return (
								<Button
									startIcon={
										<InsertDriveFileIcon sx={{ width: 20, height: 20 }} />
									}
									variant="outlined"
									sx={{
										margin: "3px",
										fontSize: 10,
										textTransform: "none",
										borderRadius: 5,
										ml: "0.5rem",
									}}
									disableRipple
								>
									<Link
										onClick={() => {
											window.open(duplicateFile.link).focus();
										}}
										variant="tableBody"
										underline="hover"
										rel="noopener"
										sx={{ fontSize: 10, textTransform: "none" }}
									>
										{duplicateFile?.name}
									</Link>
								</Button>
							);
						})}

						{props.duplicates?.duplicateFolders?.map((duplicateFolder) => {
							return (
								<Button
									startIcon={<FolderIcon sx={{ width: 20, height: 20 }} />}
									variant="outlined"
									sx={{
										margin: "3px",
										fontSize: 10,
										textTransform: "none",
										borderRadius: 5,
										ml: "0.5rem",
									}}
									disableRipple
								>
									<Link
										onClick={() => {
											window.open(duplicateFolder.link).focus();
										}}
										variant="tableBody"
										underline="hover"
										rel="noopener"
										sx={{ fontSize: 10, textTransform: "none" }}
									>
										{duplicateFolder?.name}
									</Link>
								</Button>
							);
						})}

						{/* Found {props.duplicates?.length} duplicate files in your upload
						request:
						{props.duplicates?.map((duplicateNote) => {
							return (
								<Button
									startIcon={<NotesRounded sx={{ width: 20, height: 20 }} />}
									variant="outlined"
									sx={{
										margin: "3px",
										fontSize: 10,
										textTransform: "none",
										borderRadius: 5,
										ml: "0.5rem",
									}}
									disableRipple
								>
									<Link
										onClick={() => {
											window.open(duplicateNote.link).focus();
										}}
										variant="tableBody"
										underline="hover"
										rel="noopener"
										sx={{ fontSize: 10, textTransform: "none" }}
									>
										{duplicateNote.name || duplicateNote.id}
									</Link>
								</Button>
							);
						})} */}
					</Alert>

					<Divider />
				</Collapse>
			</Box>
		);
	};

	return (
		<Box>
			{/* Notification for duplicate files uploaded */}
			<DuplicatesNotification duplicates={duplicates} />

			{/* CONTENT */}
			<Box pt="3rem" display="flex" justifyContent="center">
				{/* CHOOSE A WORKSPACE */}
				<Box textAlign="center" flexBasis="33.33%">
					<Typography variant="h4">Choose a Workspace</Typography>

					{/* PROGRESS BAR */}
					<Box pt="3rem" display="flex" justifyContent="center">
						<Box
							sx={{
								width: "50%",
								height: "10px",
								bgcolor: "primary.main",
								borderRadius: 10,
							}}
						></Box>
					</Box>

					{/* WORKSPACE BUTTONS */}
					<Box pl="6rem" pr="6rem" pt="4rem" justifyContent="center">
						{currentWorkspaces?.map((workspaceId) => (
							<ChooseWorkspaceComponent
								key={workspaceId}
								selectedWorkspace={selectedWorkspace}
								setSelectedWorkspace={setSelectedWorkspace}
								workspaceId={workspaceId}
								uploadedFiles={uploadedFiles}
								setUploadedFiles={setUploadedFiles}
								setOpenNotification={setOpenNotification}
								duplicates={duplicates}
								setDuplicates={setDuplicates}
								handleSetShowUploadBar={handleSetShowUploadBar}
							/>
						))}
					</Box>

					{/* LOCAL UPLOAD BUTTON */}
					{/* <LocalUploadCard /> */}
				</Box>

				{/* FILES UPLOADED */}
				<Box textAlign="center" flexBasis="33.33%">
					<Typography variant="h4">Files Uploaded</Typography>

					{/* PROGRESS BAR*/}
					<Box pt="3rem" display="flex" justifyContent="center">
						{/* {uploadedFiles.length === 0 ? (             
                            <Box sx={{ width: '50%', height:'10px', bgcolor:'gray', borderRadius: 10 }} > </Box>
                    ) : (
                            <Box sx={{ width: '50%', height:'10px', bgcolor:'primary.main', borderRadius: 10 }} ></Box>
                    )} */}
						{uploadedFiles.length === 0 && !showUploadBar && (
							<Box
								sx={{
									width: "50%",
									height: "10px",
									bgcolor: "gray",
									borderRadius: 10,
								}}
							>
								{" "}
							</Box>
						)}
						{uploadedFiles.length > 0 && !showUploadBar && (
							<Box
								sx={{
									width: "50%",
									height: "10px",
									bgcolor: "primary.main",
									borderRadius: 10,
								}}
							></Box>
						)}
						{showUploadBar && <UploadProgressBar />}
					</Box>

					{/* UPLOADED FILES */}
					<Box pl="6rem" pr="6rem" pt="4rem" justifyContent="left">
						{uploadedFiles.length === 0 ? (
							<SkeletonGroupFileUploaded number={4} />
						) : (
							<UploadedFileTable
								uploadedFiles={uploadedFiles}
								handleSetSummarizedFilesAndActionItems={
									handleSetSummarizedFilesAndActionItems
								}
								handleSetShowProgressBar={handleSetShowProgressBar}
							/>
						)}
					</Box>
				</Box>

				{/* SUMMARIZE FILES */}
				<Box textAlign="center" flexBasis="33.33%">
					<Typography variant="h4">Summaries & Action Items</Typography>

					{/* PROGRESS BAR*/}
					<Box pt="3rem" display="flex" justifyContent="center">
						{summarizedFiles.length === 0 && !showProgressBar && (
							<Box
								sx={{
									width: "50%",
									height: "10px",
									bgcolor: "gray",
									borderRadius: 10,
								}}
							>
								{" "}
							</Box>
						)}
						{summarizedFiles.length > 0 && !showProgressBar && (
							<Box
								sx={{
									width: "50%",
									height: "10px",
									bgcolor: "primary.main",
									borderRadius: 10,
								}}
							></Box>
						)}
						{showProgressBar && <UploadProgressBar />}
					</Box>

					{/* SUMMARIZED FILES */}
					<Box pl="6rem" pr="6rem" pt="4rem" justifyContent="left">
						{summarizedFiles.length === 0 ? (
							<SkeletonGroupSummarizeFiles number={2} />
						) : (
							<Box>
								{cards?.map((card) => (
									<FinalCardComponent
										summarizedFilesArr={summarizedFiles}
										actionItemsArr={actionItems}
										card={card}
										handleSetActionItems={handleSetActionItems}
									/>
								))}
							</Box>
						)}
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default UploadPage;
